import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarHeart, MapPin, Users, Plus, X, Ticket, ArrowRight, LayoutGrid, List as ListIcon, ArrowUp, ArrowDown, ArrowUpDown, CheckCircle2, Pencil, Trash2 } from "lucide-react";

const CATEGORIES = [
  { value: "reunion", label: "Reunion" },
  { value: "homecoming", label: "Homecoming" },
  { value: "networking", label: "Networking" },
  { value: "gala", label: "Gala" },
  { value: "lecture", label: "Lecture" },
  { value: "other", label: "Other" },
];

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-600",
  published: "bg-emerald-100 text-emerald-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800",
};

export const fmtDate = (s) => {
  if (!s) return "Date TBD";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

// An event is "past" once its end (or start) time has passed.
export const isPastEvent = (e) => { const t = e.end || e.start; return !!t && new Date(t).getTime() < Date.now(); };
// Past published events are shown as completed (explicit "completed"/"cancelled" statuses are respected).
export const effStatus = (e) => (e.status === "published" && isPastEvent(e) ? "completed" : e.status);

const EMPTY = {
  title: "", description: "", category: "reunion", start: "", end: "",
  venue: "", address: "", city: "", state: "", virtual_url: "",
  capacity: "", ticket_price: "", class_years: "", status: "draft",
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | form object
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("ud_events_view") || "grid");
  const [sort, setSort] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ud_events_sort")) || { field: "start", dir: "desc" }; }
    catch { return { field: "start", dir: "desc" }; }
  });
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const setViewMode = (v) => { setView(v); localStorage.setItem("ud_events_view", v); };

  const toggleSort = (field) => {
    setSort((prev) => {
      const next = prev.field === field ? { field, dir: prev.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" };
      localStorage.setItem("ud_events_sort", JSON.stringify(next));
      return next;
    });
  };

  const sortVal = (e, field) => {
    switch (field) {
      case "title": return (e.title || "").toLowerCase();
      case "start": return e.start ? new Date(e.start).getTime() : 0;
      case "location": return [e.location?.venue, e.location?.city].filter(Boolean).join(", ").toLowerCase();
      case "status": return (e.status || "").toLowerCase();
      case "invited": return e.summary?.invited || 0;
      case "attending": return e.summary?.attending || 0;
      case "checked_in": return e.summary?.checked_in || 0;
      default: return 0;
    }
  };

  const sortedEvents = useMemo(() => {
    const arr = [...events];
    arr.sort((a, b) => {
      const va = sortVal(a, sort.field), vb = sortVal(b, sort.field);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [events, sort]);

  const SortHeader = ({ field, label, align = "left", className = "" }) => (
    <th className={`text-${align} px-4 py-2.5 ${className}`}>
      <button
        data-testid={`sort-${field}`}
        onClick={() => toggleSort(field)}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${sort.field === field ? "text-foreground" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        {label}
        {sort.field === field
          ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
          : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );

  const eventRow = (e) => (
    <tr key={e.id} data-testid={`event-row-${e.id}`} onClick={() => navigate(`/events/${e.id}`)} className="hover:bg-muted/40 cursor-pointer">
      <td className="px-4 py-2.5 min-w-[260px]">
        <div className="font-medium flex items-center gap-1.5 whitespace-nowrap"><CalendarHeart className="h-4 w-4 text-primary shrink-0" />{e.title}</div>
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{e.category}</span>
      </td>
      <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{fmtDate(e.start)}</td>
      <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell max-w-[220px] truncate">{[e.location?.venue, e.location?.city].filter(Boolean).join(", ") || "—"}</td>
      <td className="px-4 py-2.5"><span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLES[effStatus(e)] || STATUS_STYLES.draft}`}>{effStatus(e)}</span></td>
      <td className="px-4 py-2.5 text-right font-medium">{e.summary.invited}</td>
      <td className="px-4 py-2.5 text-right font-medium">{e.summary.attending}</td>
      <td className="px-4 py-2.5 text-right font-medium">{e.summary.checked_in}</td>
      {isAdmin && (
        <td className="px-4 py-2.5" onClick={(ev) => ev.stopPropagation()}>
          <div className="flex items-center justify-end gap-1">
            <button data-testid={`event-edit-${e.id}`} title="Edit" onClick={() => openEdit(e)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`event-delete-${e.id}`} title="Delete" onClick={() => delEvent(e.id)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        </td>
      )}
    </tr>
  );

  const eventCard = (e) => (
    <div
      key={e.id}
      data-testid={`event-card-${e.id}`}
      onClick={() => navigate(`/events/${e.id}`)}
      className="text-left bg-card border border-border rounded-lg shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 hover:border-primary transition-all group flex flex-col cursor-pointer"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-accent/20 text-primary">
          <Ticket className="h-3 w-3" /> {e.category}
        </span>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLES[effStatus(e)] || STATUS_STYLES.draft}`}>{effStatus(e)}</span>
          {isAdmin && (
            <span className="flex items-center gap-1" onClick={(ev) => ev.stopPropagation()}>
              <button data-testid={`event-edit-${e.id}`} title="Edit" onClick={() => openEdit(e)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
              <button data-testid={`event-delete-${e.id}`} title="Delete" onClick={() => delEvent(e.id)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </span>
          )}
        </div>
      </div>
      <div className="flex items-start gap-2 text-primary">
        <CalendarHeart className="h-5 w-5 shrink-0 mt-0.5" />
        <span className="font-serif text-xl tracking-tight leading-snug">{e.title}</span>
      </div>
      <div className="text-sm text-muted-foreground mt-2">{fmtDate(e.start)}</div>
      {(e.location?.venue || e.location?.city) && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
          <MapPin className="h-3.5 w-3.5" /> {[e.location?.venue, e.location?.city].filter(Boolean).join(", ")}
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-border grid grid-cols-3 gap-2 text-center">
        {[["Invited", e.summary.invited], ["Attending", e.summary.attending], ["Checked-in", e.summary.checked_in]].map(([l, v]) => (
          <div key={l}>
            <div className="text-lg font-semibold flex items-center justify-center gap-1"><Users className="h-3.5 w-3.5 text-muted-foreground" />{v}</div>
            <div className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">{l}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-1 text-sm text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
        Manage <ArrowRight className="h-4 w-4" />
      </div>
    </div>
  );

  const listPanel = (list, testid) => (
    <div data-testid={testid} className="bg-card border border-border rounded-lg shadow-sm overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-muted-foreground text-xs uppercase font-mono">
          <tr>
            <SortHeader field="title" label="Event" />
            <SortHeader field="start" label="Date" />
            <SortHeader field="location" label="Location" className="hidden md:table-cell" />
            <SortHeader field="status" label="Status" />
            <SortHeader field="invited" label="Inv" align="right" />
            <SortHeader field="attending" label="Att" align="right" />
            <SortHeader field="checked_in" label="In" align="right" />
            {isAdmin && <th className="px-4 py-2.5 text-right">Actions</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">{list.map(eventRow)}</tbody>
      </table>
    </div>
  );

  const gridPanel = (list, testid) => (
    <div data-testid={testid} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{list.map(eventCard)}</div>
  );

  const panelFor = (list, testid) => (view === "list" ? listPanel(list, testid) : gridPanel(list, testid));
  const activeEvents = sortedEvents.filter((e) => effStatus(e) !== "completed");
  const completedEvents = sortedEvents.filter((e) => effStatus(e) === "completed");

  const load = () => {
    setLoading(true);
    api.get("/events").then((r) => setEvents(r.data.events)).catch((e) => toast.error(apiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openEdit = (e) => {
    const loc = e.location || {};
    setForm({
      id: e.id,
      title: e.title || "", description: e.description || "", category: e.category || "reunion",
      start: (e.start || "").slice(0, 16), end: (e.end || "").slice(0, 16),
      venue: loc.venue || "", address: loc.address || "", city: loc.city || "", state: loc.state || "", virtual_url: loc.virtual_url || "",
      capacity: e.capacity ?? "", ticket_price: e.ticket_price ?? "", class_years: (e.class_years || []).join(", "), status: e.status || "draft",
    });
  };

  const delEvent = async (id) => {
    if (!window.confirm("Delete this event permanently? This also removes its RSVPs.")) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success("Event deleted");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Event title is required"); return; }
    setSaving(true);
    const payload = {
      title: form.title, description: form.description, category: form.category,
      start: form.start, end: form.end,
      location: { venue: form.venue, address: form.address, city: form.city, state: form.state, virtual_url: form.virtual_url },
      capacity: form.capacity ? parseInt(form.capacity, 10) : null,
      ticket_price: form.ticket_price ? parseFloat(form.ticket_price) : null,
      class_years: form.class_years ? form.class_years.split(",").map((y) => parseInt(y.trim(), 10)).filter((y) => !isNaN(y)) : [],
      status: form.status,
    };
    try {
      if (form.id) {
        await api.put(`/events/${form.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/events", payload);
        toast.success("Event created");
      }
      setForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Events &amp; Reunions</div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mt-1">Events</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden">
            <button data-testid="events-view-grid" onClick={() => setViewMode("grid")} title="Grid view" className={`p-2 ${view === "grid" ? "bg-primary text-white" : "bg-card text-muted-foreground hover:bg-muted"}`}><LayoutGrid className="h-4 w-4" /></button>
            <button data-testid="events-view-list" onClick={() => setViewMode("list")} title="List view" className={`p-2 border-l border-border ${view === "list" ? "bg-primary text-white" : "bg-card text-muted-foreground hover:bg-muted"}`}><ListIcon className="h-4 w-4" /></button>
          </div>
          {isAdmin && (
            <button data-testid="new-event-btn" onClick={() => setForm({ ...EMPTY })} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
              <Plus className="h-4 w-4" /> New Event
            </button>
          )}
        </div>
      </div>

      {loading && <div className="text-muted-foreground">Loading…</div>}
      {!loading && events.length === 0 && <div className="text-muted-foreground" data-testid="events-empty">No events yet. Create your first reunion or gathering.</div>}

      {!loading && events.length > 0 && (
        <div className="space-y-8">
          {activeEvents.length > 0 && panelFor(activeEvents, "events-active")}
          {activeEvents.length === 0 && <div className="text-muted-foreground text-sm" data-testid="events-active-empty">No upcoming or active events.</div>}
          {completedEvents.length > 0 && (
            <div data-testid="events-completed-panel">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="h-5 w-5 text-blue-600" />
                <h2 className="font-serif text-xl tracking-tight">Completed Events</h2>
                <span className="text-sm text-muted-foreground">({completedEvents.length})</span>
              </div>
              {panelFor(completedEvents, "events-completed")}
            </div>
          )}
        </div>
      )}

      {form && createPortal((
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60" onMouseDown={(ev) => { if (ev.target === ev.currentTarget) setForm(null); }}>
          <div className="bg-card w-full max-w-2xl rounded-lg shadow-xl border border-border flex flex-col" style={{ maxHeight: "calc(100vh - 2rem)", overflow: "hidden" }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <h2 className="font-serif text-lg tracking-tight">{form.id ? "Edit Event" : "New Event"}</h2>
              <button data-testid="event-form-close" onClick={() => setForm(null)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4 flex-1 min-h-0">
              <Field label="Title">
                <input data-testid="event-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} className="ud-input" placeholder="Homecoming Weekend" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <select data-testid="event-category-select" value={form.category} onChange={(e) => set("category", e.target.value)} className="ud-input">
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select data-testid="event-status-select" value={form.status} onChange={(e) => set("status", e.target.value)} className="ud-input">
                    {["draft", "published", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Description">
                <textarea data-testid="event-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="ud-input" placeholder="Details about the event…" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Starts"><input data-testid="event-start-input" type="datetime-local" value={form.start} onChange={(e) => set("start", e.target.value)} className="ud-input" /></Field>
                <Field label="Ends"><input data-testid="event-end-input" type="datetime-local" value={form.end} onChange={(e) => set("end", e.target.value)} className="ud-input" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Venue"><input value={form.venue} onChange={(e) => set("venue", e.target.value)} className="ud-input" placeholder="Braniff Mall" /></Field>
                <Field label="Address"><input value={form.address} onChange={(e) => set("address", e.target.value)} className="ud-input" placeholder="1845 E Northgate Dr" /></Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="City"><input value={form.city} onChange={(e) => set("city", e.target.value)} className="ud-input" placeholder="Irving" /></Field>
                <Field label="State"><input value={form.state} onChange={(e) => set("state", e.target.value)} className="ud-input" placeholder="TX" /></Field>
                <Field label="Virtual URL"><input value={form.virtual_url} onChange={(e) => set("virtual_url", e.target.value)} className="ud-input" placeholder="https://…" /></Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Capacity"><input type="number" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className="ud-input" placeholder="200" /></Field>
                <Field label="Ticket ($)"><input type="number" step="0.01" value={form.ticket_price} onChange={(e) => set("ticket_price", e.target.value)} className="ud-input" placeholder="0.00" /></Field>
                <Field label="Class years"><input value={form.class_years} onChange={(e) => set("class_years", e.target.value)} className="ud-input" placeholder="1998, 1999" /></Field>
              </div>
              <p className="text-xs text-muted-foreground">Leave class years blank to invite all alumni with an email on file.</p>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-3 shrink-0">
              <button onClick={() => setForm(null)} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
              <button data-testid="event-save-btn" onClick={save} disabled={saving} className="px-5 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{saving ? "Saving…" : (form.id ? "Save Changes" : "Create Event")}</button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
